import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search, ArrowLeft, Truck, Package, CheckCircle2, Clock, XCircle, Loader2,
  AlertTriangle, ChevronRight, MapPin, User, FileText, Ban, PlayCircle, PackageCheck, Send,
  Download, Pencil, DollarSign, Plus, Trash2, X, Save, ScanLine, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtT = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const downloadPdf = (b64, filename) => {
  try {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { toast.error('Could not open document.'); }
};

export const SM = {
  draft: { label: 'Draft', color: '#64748B' },
  submitted: { label: 'Submitted', color: '#F59E0B' },
  pending: { label: 'Submitted', color: '#F59E0B' },
  processing: { label: 'Processing', color: '#3B82F6' },
  ready_to_ship: { label: 'Ready to Ship', color: '#6366F1' },
  shipped: { label: 'Shipped', color: '#8B5CF6' },
  delivered: { label: 'Delivered', color: '#10B981' },
  cancelled: { label: 'Cancelled', color: '#EF4444' },
};
const sm = (s) => SM[s] || { label: (s || '').replace('_', ' '), color: '#64748B' };
const Pill = ({ s }) => { const m = sm(s); return <span className="inline-block text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>; };
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

// Fulfillment actions available to admin per current status
const ADMIN_ACTIONS = {
  submitted: [{ action: 'begin_processing', label: 'Begin Processing', icon: PlayCircle }],
  pending: [{ action: 'begin_processing', label: 'Begin Processing', icon: PlayCircle }],
  processing: [{ action: 'ready_to_ship', label: 'Mark Ready to Ship', icon: PackageCheck }],
  ready_to_ship: [{ action: 'ship', label: 'Mark Shipped', icon: Truck }],
  shipped: [{ action: 'deliver', label: 'Mark Delivered', icon: CheckCircle2 }],
};

const ShipDialog = ({ open, onClose, onConfirm, busy }) => {
  const [carrier, setCarrier] = useState('UPS');
  const [tracking, setTracking] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  if (!open) return null;
  const cls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:border-[#C6A15B]/60 focus:outline-none";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ship-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#14171B] border border-white/[0.08] rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Truck className="w-5 h-5 text-[#C6A15B]" /> Add Shipment</h3>
        <div className="space-y-3">
          <div><label className="block text-xs text-slate-400 mb-1">Carrier</label>
            <select className={cls} value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ship-carrier">
              {['UPS', 'FedEx', 'USPS', 'DHL', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label className="block text-xs text-slate-400 mb-1">Tracking Number</label>
            <input className={cls} value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="ship-tracking" placeholder="e.g. 1Z999AA10123456784" /></div>
          <div><label className="block text-xs text-slate-400 mb-1">Ship Date</label>
            <input type="date" className={cls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="ship-date" /></div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 bg-white/[0.05] hover:bg-white/[0.1] text-slate-200 text-sm font-medium px-4 py-2.5 rounded-xl">Cancel</button>
          <button disabled={busy || !tracking} onClick={() => onConfirm({ carrier, tracking_number: tracking, ship_date: date })}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl disabled:opacity-60" data-testid="ship-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Confirm Shipment
          </button>
        </div>
      </div>
    </div>
  );
};

const EditOrderPanel = ({ order, onClose, onSaved }) => {
  const [items, setItems] = useState((order.items || []).map((it) => ({ ...it, qty: it.quantity })));
  const [products, setProducts] = useState([]);
  const [pq, setPq] = useState('');
  const [nc, setNc] = useState({ name: '', price: '', quantity: 1 });
  const [ship, setShip] = useState(order.shipping_address || '');
  const [notes, setNotes] = useState(order.special_instructions || '');
  const [discount, setDiscount] = useState(order.discount_percentage || 0);
  const [busy, setBusy] = useState(false);
  const cls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-[#C6A15B]/60 focus:outline-none";

  useEffect(() => {
    axios.get(`${API}/api/products?limit=300&customer_id=${order.customer_id}`).then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, [order.customer_id]);

  const setQty = (pid, q) => setItems((xs) => xs.map((x) => x.product_id === pid ? { ...x, qty: Math.max(1, q) } : x));
  const removeItem = (pid) => setItems((xs) => xs.filter((x) => x.product_id !== pid));
  const addProduct = (p) => setItems((xs) => xs.find((x) => x.product_id === p.product_id)
    ? xs.map((x) => x.product_id === p.product_id ? { ...x, qty: x.qty + 1 } : x)
    : [...xs, { product_id: p.product_id, product_name: p.name, qty: 1, effective_unit_price: p.discounted_price ?? p.price, is_custom: false }]);
  const addCustom = () => { if (!nc.name || !nc.price) return toast.error('Name and price required'); setItems((xs) => [...xs, { product_id: `custom_new_${Date.now()}`, product_name: nc.name, qty: parseInt(nc.quantity) || 1, price: parseFloat(nc.price), effective_unit_price: parseFloat(nc.price), is_custom: true, custom_notes: '' }]); setNc({ name: '', price: '', quantity: 1 }); };

  const save = async () => {
    setBusy(true);
    try {
      const payloadItems = items.map((it) => ({
        product_id: it.product_id, quantity: it.qty, is_custom: !!it.is_custom,
        product_name: it.product_name, custom_price: it.is_custom ? (it.price ?? it.effective_unit_price) : undefined,
        notes: it.custom_notes,
      }));
      await axios.put(`${API}/api/orders/${order.order_id}`, { items: payloadItems, shipping_address: ship, special_instructions: notes });
      if (Number(discount) !== Number(order.discount_percentage || 0)) {
        await axios.put(`${API}/api/orders/${order.order_id}/discount`, { discount_percentage: Number(discount) });
      }
      toast.success('Order updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const filtered = pq.trim() ? products.filter((p) => (p.name || '').toLowerCase().includes(pq.toLowerCase())) : products.slice(0, 8);

  return (
    <div className="fixed inset-0 z-[60]" data-testid="edit-order-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-[#0C0E11] border-l border-white/[0.08] overflow-y-auto">
        <div className="sticky top-0 bg-[#0C0E11]/90 backdrop-blur-xl border-b border-white/[0.06] px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2"><Pencil className="w-4 h-4 text-[#C6A15B]" /> Edit Order {order.order_number}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.product_id} className="flex items-center gap-2 text-sm bg-black/30 rounded-lg px-3 py-2">
                <div className="min-w-0 flex-1"><p className="text-slate-100 truncate">{it.product_name}{it.is_custom && <span className="text-[10px] text-amber-300 ml-1">custom</span>}</p>
                  <p className="text-slate-500 text-xs">{money(it.effective_unit_price ?? it.price)} each</p></div>
                <input type="number" min="1" value={it.qty} onChange={(e) => setQty(it.product_id, parseInt(e.target.value) || 1)} className="w-14 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs" data-testid={`edit-qty-${it.product_id}`} />
                <button onClick={() => removeItem(it.product_id)} className="text-slate-500 hover:text-red-400" data-testid={`edit-remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Add Product</label>
            <input className={cls} placeholder="Search…" value={pq} onChange={(e) => setPq(e.target.value)} data-testid="edit-add-search" />
            <div className="max-h-36 overflow-y-auto mt-1 space-y-1">
              {filtered.map((p) => (
                <button key={p.product_id} onClick={() => addProduct(p)} data-testid={`edit-add-${p.product_id}`} className="w-full flex justify-between text-left px-3 py-1.5 rounded hover:bg-white/[0.04] text-sm text-slate-200">
                  <span className="truncate">{p.name}</span><span className="text-[#C6A15B] text-xs">{money(p.discounted_price ?? p.price)}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Add Custom Item</label>
            <div className="flex gap-2">
              <input className={cls} placeholder="Description" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} data-testid="edit-custom-name" />
              <input className={`${cls} w-24`} type="number" placeholder="Price" value={nc.price} onChange={(e) => setNc({ ...nc, price: e.target.value })} data-testid="edit-custom-price" />
              <button onClick={addCustom} className="bg-white/[0.06] text-slate-200 text-sm px-3 rounded-lg" data-testid="edit-custom-add"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
          <div><label className="block text-xs text-slate-400 mb-1">Discount %</label>
            <input className={cls} type="number" min="0" max="100" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="edit-discount" /></div>
          <div><label className="block text-xs text-slate-400 mb-1">Shipping Address</label>
            <input className={cls} value={ship} onChange={(e) => setShip(e.target.value)} data-testid="edit-shipping" /></div>
          <div><label className="block text-xs text-slate-400 mb-1">Order Notes</label>
            <textarea className={cls} rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" /></div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 bg-white/[0.05] text-slate-200 text-sm px-4 py-2.5 rounded-xl">Cancel Changes</button>
            <button disabled={busy} onClick={save} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C6A15B] text-black font-medium text-sm px-4 py-2.5 rounded-xl disabled:opacity-60" data-testid="edit-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SerialCard = ({ order, onSaved }) => {
  const catalogItems = (order.items || []).filter((it) => !it.is_custom && !String(it.product_id || '').startsWith('custom_'));
  const buildSlots = (existing) => {
    const byProduct = {};
    (existing || []).forEach((s) => { (byProduct[s.product_id] = byProduct[s.product_id] || []).push(s.serial_number); });
    const slots = [];
    catalogItems.forEach((it) => {
      const vals = byProduct[it.product_id] || [];
      for (let i = 0; i < (it.quantity || 0); i++) slots.push({ product_id: it.product_id, product_name: it.product_name, value: vals[i] || '' });
    });
    return slots;
  };
  const [slots, setSlots] = useState(buildSlots(order.serial_numbers));
  const [saving, setSaving] = useState(false);
  const refs = React.useRef([]);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/orders/${order.order_id}/serials`); setSlots(buildSlots(r.data.serials)); }
      catch (e) { /* keep order summary */ }
    })();
    // eslint-disable-next-line
  }, [order.order_id]);

  const setVal = (i, v) => setSlots((s) => s.map((x, idx) => idx === i ? { ...x, value: v.toUpperCase() } : x));
  const onKey = (e, i) => { if (e.key === 'Enter') { e.preventDefault(); const n = refs.current[i + 1]; if (n) n.focus(); } };
  const filled = slots.filter((s) => s.value.trim()).length;

  const save = async () => {
    const serials = slots.filter((s) => s.value.trim()).map((s) => ({ serial_number: s.value.trim(), product_id: s.product_id }));
    const seen = new Set();
    for (const s of serials) { if (seen.has(s.serial_number)) { toast.error(`Duplicate serial: ${s.serial_number}`); return; } seen.add(s.serial_number); }
    setSaving(true);
    try { const r = await axios.post(`${API}/api/orders/${order.order_id}/serials`, { serials }); toast.success(`${r.data.assigned} serial(s) saved`); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save serials'); }
    finally { setSaving(false); }
  };

  if (catalogItems.length === 0) return null;
  let idx = -1;
  return (
    <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6" data-testid="serial-capture-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2"><ScanLine className="w-4 h-4 text-slate-400" /> Serial Numbers</h3>
        <span className="text-xs text-slate-400" data-testid="serial-progress">{filled} of {slots.length} assigned</span>
      </div>
      <div className="space-y-4">
        {catalogItems.map((it) => (
          <div key={it.product_id}>
            <p className="text-slate-300 text-sm mb-2 flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-[#C6A15B]" /> {it.product_name} <span className="text-slate-500">· qty {it.quantity}</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Array.from({ length: it.quantity || 0 }).map((_, j) => {
                idx += 1; const myIdx = idx;
                return (
                  <input key={j} ref={(el) => (refs.current[myIdx] = el)} value={slots[myIdx]?.value || ''}
                    onChange={(e) => setVal(myIdx, e.target.value)} onKeyDown={(e) => onKey(e, myIdx)}
                    placeholder={`Scan or enter serial #${j + 1}`} data-testid={`serial-input-${it.product_id}-${j}`}
                    className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 font-mono placeholder:text-slate-600 placeholder:font-sans focus:border-[#C6A15B]/60 focus:outline-none" />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="serial-save-btn"
        className="mt-4 inline-flex items-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2 rounded-xl disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Serials
      </button>
    </div>
  );
};

const OrderDetail = ({ orderId, role, onBack }) => {
  const [order, setOrder] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showShip, setShowShip] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const getDoc = async (kind) => {
    try {
      const r = await axios.get(`${API}/api/orders/${orderId}/${kind === 'invoice' ? 'invoice' : 'packing-slip'}`);
      downloadPdf(kind === 'invoice' ? r.data.invoice_pdf : r.data.packing_slip_pdf, `${order.order_number}-${kind}.pdf`);
    } catch (e) { toast.error('Could not load document.'); }
  };
  const togglePaid = async () => {
    const next = (order.payment_status === 'paid') ? 'pending' : 'paid';
    try { await axios.put(`${API}/api/orders/${orderId}/payment-status?payment_status=${next}`); toast.success(`Marked ${next}`); await load(); }
    catch (e) { toast.error('Could not update payment status.'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, a] = await Promise.all([
        axios.get(`${API}/api/orders/${orderId}`),
        axios.get(`${API}/api/orders/${orderId}/activity`),
      ]);
      setOrder(o.data); setActivity(a.data.activity || []);
    } catch (e) { toast.error('Could not load order.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const doAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/orders/${orderId}/transition`, { action, ...extra });
      toast.success(res.data.message || 'Order updated');
      setShowShip(false);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'pricing_changed') {
        if (window.confirm(`${d.message}\n\nOld total: $${d.old_total}\nNew total: $${d.new_total}\n\nSubmit at the updated total?`)) {
          return doAction(action, { ...extra, force: true });
        }
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed'));
      }
    } finally { setBusy(false); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
  if (!order) return <p className="text-slate-500">Order not found.</p>;

  const st = order.status_normalized || order.status;
  const actions = role === 'tc_admin' ? (ADMIN_ACTIONS[st] || [])
    : (role === 'sales_rep' && st === 'draft' ? [{ action: 'submit', label: 'Submit to TC', icon: Send }] : []);
  const canCancel = role === 'tc_admin' ? !['delivered', 'cancelled'].includes(st)
    : (role === 'sales_rep' && st === 'draft');

  return (
    <div className="space-y-5" data-testid="order-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="order-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>

      {/* Header */}
      <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-white font-mono">{order.order_number}</h2>
              <Pill s={st} />
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] text-slate-300">
                {order.order_source === 'SALES_REP' ? `Sales Rep · ${order.sales_rep_name || '—'}` : 'TC Direct'}
              </span>
            </div>
            <p className="text-slate-400 text-sm mt-1">{order.customer_company || order.customer_name} · {fmt(order.created_at)}</p>
          </div>
          <div className="text-right">
            <p className="text-slate-500 text-xs">Order Total</p>
            <p className="text-2xl font-semibold text-white">{money(order.total_amount)}</p>
          </div>
        </div>
        {/* Actions */}
        {(actions.length > 0 || canCancel) && (
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-white/[0.06]">
            {actions.map((a) => (
              <button key={a.action} disabled={busy}
                onClick={() => a.action === 'ship' ? setShowShip(true) : doAction(a.action)}
                data-testid={`action-${a.action}`}
                className="inline-flex items-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2 rounded-xl disabled:opacity-60">
                <a.icon className="w-4 h-4" /> {a.label}
              </button>
            ))}
            {canCancel && (
              <button disabled={busy} onClick={() => { if (window.confirm('Cancel this order?')) doAction('cancel'); }}
                data-testid="action-cancel"
                className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-medium text-sm px-4 py-2 rounded-xl disabled:opacity-60">
                <Ban className="w-4 h-4" /> Cancel Order
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: items + shipping */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2"><Package className="w-4 h-4 text-slate-400" /> Order Items</h3>
              {role === 'tc_admin' && !['delivered', 'cancelled'].includes(st) && (
                <button onClick={() => setShowEdit(true)} data-testid="edit-order-btn"
                  className="inline-flex items-center gap-1.5 text-xs bg-white/[0.05] hover:bg-white/[0.1] text-white px-3 py-1.5 rounded-lg">
                  <Pencil className="w-3.5 h-3.5" /> Edit Order
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0" data-testid={`detail-item-${i}`}>
                  <div className="min-w-0">
                    <p className="text-slate-100 text-sm flex items-center gap-2">
                      {it.product_name}
                      {it.is_custom && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Custom{it.needs_review ? ' · review' : ''}</span>}
                    </p>
                    <p className="text-slate-500 text-xs">{it.quantity} × {money(it.effective_unit_price ?? it.price)}</p>
                  </div>
                  <span className="text-white font-medium text-sm">{money(it.effective_total ?? it.total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-400"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.discount_amount > 0 && <div className="flex justify-between text-slate-400"><span>Discount</span><span className="text-emerald-400">−{money(order.discount_amount)}</span></div>}
              {order.custom_items_total > 0 && <div className="flex justify-between text-slate-400"><span>Custom Items</span><span>{money(order.custom_items_total)}</span></div>}
              <div className="flex justify-between text-white font-semibold text-base pt-1"><span>Total</span><span>{money(order.total_amount)}</span></div>
            </div>
          </div>

          <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> Shipping & Fulfillment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><p className="text-slate-500 text-xs">Address</p><p className="text-slate-200">{order.shipping_address || '—'}</p></div>
              <div><p className="text-slate-500 text-xs">Carrier</p><p className="text-slate-200">{order.carrier || '—'}</p></div>
              <div><p className="text-slate-500 text-xs">Tracking</p><p className="text-slate-200 font-mono">{order.tracking_number || order.shipping_tracking || '—'}</p></div>
              <div><p className="text-slate-500 text-xs">Ship Date</p><p className="text-slate-200">{fmt(order.ship_date)}</p></div>
            </div>
            {order.special_instructions && <div className="mt-3 pt-3 border-t border-white/[0.06]"><p className="text-slate-500 text-xs mb-1">Order Notes</p><p className="text-slate-300 text-sm">{order.special_instructions}</p></div>}
          </div>

          {role === 'tc_admin' && order.inventory_committed && !['cancelled'].includes(st) && (
            <SerialCard order={order} onSaved={load} />
          )}

          {role === 'tc_admin' && (
            <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6" data-testid="documents-card">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" /> Documents</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => getDoc('invoice')} data-testid="doc-invoice"
                  className="inline-flex items-center gap-2 bg-white/[0.05] hover:bg-white/[0.1] text-slate-200 text-sm px-4 py-2 rounded-xl"><Download className="w-4 h-4" /> Invoice PDF</button>
                <button onClick={() => getDoc('packing')} data-testid="doc-packing"
                  className="inline-flex items-center gap-2 bg-white/[0.05] hover:bg-white/[0.1] text-slate-200 text-sm px-4 py-2 rounded-xl"><Download className="w-4 h-4" /> Packing Slip PDF</button>
                <button onClick={togglePaid} data-testid="doc-payment-toggle"
                  className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl ${order.payment_status === 'paid' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                  <DollarSign className="w-4 h-4" /> {order.payment_status === 'paid' ? 'Paid' : 'Mark as Paid'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: timeline */}
        <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Order Activity</h3>
          {activity.length === 0 ? <p className="text-slate-500 text-sm">No activity yet.</p> : (
            <ol className="space-y-3">
              {activity.map((e) => (
                <li key={e.activity_id} className="relative pl-4 border-l border-white/10">
                  <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#C6A15B]" />
                  <p className="text-slate-200 text-sm">{e.label}</p>
                  <p className="text-[11px] text-slate-500">{fmtT(e.timestamp)} · {e.performed_by_name}</p>
                  {e.metadata?.tracking_number && <p className="text-[11px] text-slate-400 font-mono">{e.metadata.carrier} {e.metadata.tracking_number}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <ShipDialog open={showShip} onClose={() => setShowShip(false)} busy={busy} onConfirm={(d) => doAction('ship', d)} />
      {showEdit && <EditOrderPanel order={order} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
};

const TABS = [
  { k: 'all', l: 'All' }, { k: 'needs_attention', l: 'Needs Attention' }, { k: 'draft', l: 'Drafts' },
  { k: 'submitted', l: 'Submitted' }, { k: 'processing', l: 'Processing' }, { k: 'ready_to_ship', l: 'Ready' },
  { k: 'shipped', l: 'Shipped' }, { k: 'delivered', l: 'Delivered' }, { k: 'cancelled', l: 'Cancelled' },
];

export const OrderQueue = ({ user }) => {
  const role = user?.role;
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [sourceF, setSourceF] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all') params.set('status', tab);
      if (sourceF !== 'all') params.set('source', sourceF);
      if (q.trim()) params.set('q', q.trim());
      const [o, c] = await Promise.all([
        axios.get(`${API}/api/orders?limit=200&${params.toString()}`),
        axios.get(`${API}/api/orders/counts`),
      ]);
      setOrders(o.data.orders || []);
      setCounts(c.data || {});
    } catch (e) { toast.error('Could not load orders.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, sourceF]);

  const filtered = useMemo(() => orders, [orders]);

  if (selected) return <OrderDetail orderId={selected} role={role} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-5" data-testid="order-queue">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-white">{role === 'tc_admin' ? 'Orders' : 'My Orders'}</h2>
          <p className="text-slate-500 text-sm mt-0.5">{counts.all ?? 0} orders · {counts.needs_attention ?? 0} need attention</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`queue-tab-${t.k}`}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === t.k ? 'bg-[#C6A15B] text-black' : 'bg-white/[0.04] text-slate-400 hover:text-white'}`}>
            {t.l}{counts[t.k] != null && t.k !== 'all' ? ` ${counts[t.k]}` : ''}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, customer, rep, tracking…"
            className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none"
            data-testid="queue-search" />
        </form>
        {role === 'tc_admin' && (
          <div className="flex gap-1 bg-black/30 rounded-lg p-0.5 self-start">
            {[{ k: 'all', l: 'All' }, { k: 'TC_DIRECT', l: 'TC Direct' }, { k: 'SALES_REP', l: 'Sales Rep' }].map((f) => (
              <button key={f.k} onClick={() => setSourceF(f.k)} data-testid={`queue-source-${f.k}`}
                className={`px-3 py-1.5 rounded-md text-xs ${sourceF === f.k ? 'bg-[#C6A15B] text-black font-medium' : 'text-slate-400 hover:text-white'}`}>{f.l}</button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16 bg-[#14171B] border border-white/[0.06] rounded-2xl" data-testid="queue-empty">
            <FileText className="w-10 h-10 text-slate-600 mb-3" />
            <p className="text-slate-300 font-medium">No orders here.</p>
            <p className="text-slate-500 text-sm mt-1">Orders will appear as they come in.</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block bg-[#14171B] border border-white/[0.06] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500 text-xs border-b border-white/[0.06] bg-black/20">
                  <th className="text-left font-medium px-5 py-3">Order #</th>
                  <th className="text-left font-medium px-5 py-3">Customer</th>
                  {role === 'tc_admin' && <th className="text-left font-medium px-5 py-3">Source</th>}
                  <th className="text-left font-medium px-5 py-3">Date</th>
                  <th className="text-right font-medium px-5 py-3">Total</th>
                  <th className="text-center font-medium px-5 py-3">Status</th>
                  <th className="px-5 py-3"></th>
                </tr></thead>
                <tbody>
                  {filtered.map((o) => (
                    <tr key={o.order_id} onClick={() => setSelected(o.order_id)} data-testid={`order-row-${o.order_id}`}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer">
                      <td className="px-5 py-3 font-mono text-[#C6A15B]">{o.order_number}</td>
                      <td className="px-5 py-3 text-slate-200 truncate max-w-[200px]">{o.customer_company || o.customer_name}</td>
                      {role === 'tc_admin' && <td className="px-5 py-3 text-slate-400 text-xs">{o.order_source === 'SALES_REP' ? `Rep · ${o.sales_rep_name || '—'}` : 'TC Direct'}</td>}
                      <td className="px-5 py-3 text-slate-400">{fmt(o.created_at)}</td>
                      <td className="px-5 py-3 text-right text-white font-medium">{money(o.total_amount)}</td>
                      <td className="px-5 py-3 text-center"><Pill s={o.status_normalized || o.status} /></td>
                      <td className="px-5 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-600 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3">
              {filtered.map((o) => (
                <button key={o.order_id} onClick={() => setSelected(o.order_id)} data-testid={`order-card-${o.order_id}`}
                  className="w-full text-left bg-[#14171B] border border-white/[0.06] rounded-xl p-4 hover:border-[#C6A15B]/40">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[#C6A15B] text-sm">{o.order_number}</span>
                    <Pill s={o.status_normalized || o.status} />
                  </div>
                  <p className="text-slate-100 text-sm mt-1 truncate">{o.customer_company || o.customer_name}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                    <span>{fmt(o.created_at)}{role === 'tc_admin' && o.order_source === 'SALES_REP' ? ` · Rep: ${o.sales_rep_name || '—'}` : ''}</span>
                    <span className="text-white font-medium">{money(o.total_amount)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
    </div>
  );
};

export default OrderQueue;
